import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import PlaceIcon from '@mui/icons-material/Place'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { AuthedImage } from '../../components/AuthedImage'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import {
  useDeletePhoto,
  useSitePhotos,
  useUploadPhoto,
  workProgressApi,
} from '../../api/execution'
import { ErrorBlock, LoadingBlock } from '../../components/Feedback'

/**
 * Photographs for the day, optionally tied to the work line they evidence.
 *
 * <p>Images are fetched through the authenticated API rather than a plain img src, because
 * site photographs are not public and the endpoint requires a bearer token.</p>
 */
export function PhotosTab({ reportId, editable }: { reportId: string; editable: boolean }) {
  const photos = useSitePhotos(reportId)
  const upload = useUploadPhoto(reportId)
  const remove = useDeletePhoto(reportId)
  const workLines = workProgressApi.useList(reportId)
  const [open, setOpen] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Evidence for the work recorded today. Link a photo to a work line and it becomes
          proof of that quantity.
        </Typography>
        {editable && (
          <Button size="small" variant="contained" startIcon={<PhotoCameraIcon />} onClick={() => setOpen(true)}>
            Add photo
          </Button>
        )}
      </Box>

      {photos.isLoading && <LoadingBlock />}
      {photos.error && <ErrorBlock error={photos.error} />}
      {upload.error && <ErrorBlock error={upload.error} title="Could not upload the photo" />}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
        {(photos.data ?? []).map((p) => (
          <Card key={p.id} variant="outlined">
            <AuthedImage
              src={p.url}
              alt={p.description ?? p.fileName}
              height={150}
              onClick={() => setPreview(p.url)}
            />
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="body2" noWrap title={p.description}>
                {p.description || p.fileName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {new Date(p.serverReceivedAt).toLocaleString()}
                </Typography>
                {p.latitude != null && (
                  <Tooltip title={`${p.latitude}, ${p.longitude}`}>
                    <PlaceIcon fontSize="inherit" color="action" />
                  </Tooltip>
                )}
                {editable && (
                  <IconButton size="small" onClick={() => remove.mutate(p.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {(photos.data?.length ?? 0) === 0 && !photos.isLoading && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No photographs yet.
        </Typography>
      )}

      <UploadDialog
        open={open}
        pending={upload.isPending}
        workLines={(workLines.data ?? []).map((w) => ({
          id: w.id,
          label: `${w.workItemCode} — ${w.todayCompletedQuantity} ${w.uomCode ?? ''}`,
        }))}
        onClose={() => setOpen(false)}
        onUpload={async (file, description, workProgressId) => {
          await upload.mutateAsync({ file, description, workProgressId })
          setOpen(false)
        }}
      />

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} maxWidth="lg">
        {preview && (
          <AuthedImage
            src={preview}
            height="auto"
            sx={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
          />
        )}
      </Dialog>
    </>
  )
}

function UploadDialog({
  open,
  pending,
  workLines,
  onClose,
  onUpload,
}: {
  open: boolean
  pending: boolean
  workLines: { id: string; label: string }[]
  onClose: () => void
  onUpload: (file: File, description?: string, workProgressId?: string) => Promise<void>
}) {
  const [file, setFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')
  const [workProgressId, setWorkProgressId] = useState('')

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a site photograph</DialogTitle>
      <DialogContent dividers>
        <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />} fullWidth sx={{ mb: 2 }}>
          {file ? file.name : 'Choose or take a photo'}
          {/* capture hints a phone browser straight to the camera */}
          <input hidden type="file" accept="image/*" capture="environment"
                 onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Button>
        {file && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {Math.round(file.size / 1024)} KB · {file.type}
          </Typography>
        )}
        <TextField label="Description" fullWidth value={description}
                   onChange={(e) => setDescription(e.target.value)} sx={{ mb: 2 }}
                   placeholder="Block work 2nd floor east wing" />
        <TextField select label="Evidence for work line" fullWidth value={workProgressId}
                   onChange={(e) => setWorkProgressId(e.target.value)}
                   helperText="Optional — links the photo to a recorded quantity">
          <MenuItem value="">Not linked</MenuItem>
          {workLines.map((w) => (
            <MenuItem key={w.id} value={w.id}>{w.label}</MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pending || !file}
          onClick={() => file && onUpload(file, description || undefined, workProgressId || undefined)}
        >
          {pending ? 'Uploading...' : 'Upload photo'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
